<!-- ── Hero ── -->
<section class="hero">
  <div class="hero-inner">
    <div class="hero-left">
      <h1>Timesheet Report</h1>
      <p class="hero-sub">View and export timesheet entries by project</p>
    </div>
    <div class="hero-right">
      <div class="hero-kpi" *ngIf="timesheetEntries.length > 0">
        <mat-icon>schedule</mat-icon>
        <div class="kpi-text">
          <span class="kpi-value">{{ calculateTotalHours() }}</span>
          <span class="kpi-label">Hours</span>
        </div>
      </div>
      <div class="hero-kpi accent" *ngIf="timesheetEntries.length > 0">
        <mat-icon>list_alt</mat-icon>
        <div class="kpi-text">
          <span class="kpi-value">{{ timesheetEntries.length }}</span>
          <span class="kpi-label">Entries</span>
        </div>
      </div>
    </div>
  </div>
</section>

<div class="report-shell">

  <!-- Status bars -->
  <div *ngIf="error" class="status-bar error">
    <mat-icon>error</mat-icon> {{ error }}
  </div>
  <div *ngIf="loading" class="status-bar info">
    <mat-icon class="spinner">hourglass_empty</mat-icon> Loading timesheet data…
  </div>

  <!-- Project Selector Card -->
  <div class="card selector-card">
    <div class="card-header">
      <mat-icon>folder_open</mat-icon>
      <h3>Select Project</h3>
    </div>
    <div class="card-body selector-body">
      <div class="select-wrap">
        <mat-icon class="select-icon">business</mat-icon>
        <select
          id="project-select"
          [(ngModel)]="selectedProject"
          (ngModelChange)="loadTimesheetEntries()">
          <option [ngValue]="null" disabled>Choose a project…</option>
          <option *ngFor="let project of projects" [ngValue]="project.projectNumber">
            {{ project.projectNumber }} — {{ project.projectName }}
          </option>
        </select>
      </div>
      <div *ngIf="projectsLoading" class="inline-loading">
        <mat-icon class="spinner">hourglass_empty</mat-icon> Loading projects…
      </div>
    </div>
  </div>

  <!-- Results section -->
  <div *ngIf="selectedProject && timesheetEntries.length > 0 && !loading">

    <!-- Toolbar -->
    <div class="toolbar">
      <h2 class="results-title">
        {{ getProjectName(selectedProject) }}
        <span class="results-badge">Project #{{ selectedProject }}</span>
      </h2>
      <div class="toolbar-actions">
        <button class="btn-outline" (click)="showFilterModal = true">
          <mat-icon>filter_list</mat-icon> Filter
        </button>
        <button class="btn-primary" (click)="downloadCsv()">
          <mat-icon>download</mat-icon> Download CSV
        </button>
      </div>
    </div>

    <!-- Entries Table Card -->
    <div class="card">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Work Code</th>
              <th>Job Type</th>
              <th>Employee</th>
              <th>Hours</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let entry of displayedEntries">
              <td class="cell-date">{{ entry.date | date: 'MMM dd, yyyy' }}</td>
              <td>{{ getWorkCodeDescription(entry.workCode) }}</td>
              <td>{{ entry.jobType || '—' }}</td>
              <td>{{ getUsernameById(entry.employee) }}</td>
              <td class="cell-hours">{{ entry.hours }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <span class="footer-label">{{ timesheetEntries.length }} entr{{ timesheetEntries.length !== 1 ? 'ies' : 'y' }}</span>
        <span class="footer-total">Total: <strong>{{ calculateTotalHours() }} hrs</strong></span>
      </div>
    </div>

    <!-- Pagination -->
    <div *ngIf="totalPages > 1" class="pagination">
      <button class="page-btn" (click)="prevPage()" [disabled]="currentPage === 1">
        <mat-icon>chevron_left</mat-icon>
      </button>
      <ng-container *ngFor="let p of [].constructor(totalPages); let i = index">
        <button class="page-btn" (click)="goToPage(i+1)" [class.active]="currentPage === (i+1)">{{ i+1 }}</button>
      </ng-container>
      <button class="page-btn" (click)="nextPage()" [disabled]="currentPage === totalPages">
        <mat-icon>chevron_right</mat-icon>
      </button>
    </div>
  </div>

  <!-- Empty state -->
  <div class="empty-state" *ngIf="selectedProject && timesheetEntries.length === 0 && !loading && !error">
    <mat-icon>inbox</mat-icon>
    <p>No timesheet entries found for this project</p>
  </div>

</div>

<!-- Filter modal -->
<div *ngIf="showFilterModal" class="modal-backdrop">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="filterModalTitle">
    <div class="modal-header">
      <mat-icon class="modal-icon">filter_list</mat-icon>
      <h4 id="filterModalTitle">Filter Entries</h4>
    </div>

    <div class="filter-grid">
      <div class="filter-row first-row">
        <div class="filter-field">
          <label>Start Date</label>
          <p class="field-hint">From this date</p>
          <div class="input-wrap">
            <mat-icon class="input-icon">calendar_today</mat-icon>
            <input type="date" [(ngModel)]="startDate" />
          </div>
        </div>

        <div class="filter-field">
          <label>End Date</label>
          <p class="field-hint">Up to this date</p>
          <div class="input-wrap">
            <mat-icon class="input-icon">event</mat-icon>
            <input type="date" [(ngModel)]="endDate" />
          </div>
        </div>

        <div class="filter-field">
          <label>Employee</label>
          <p class="field-hint">Filter by employee</p>
          <div class="input-wrap select-input-wrap">
            <mat-icon class="input-icon">person</mat-icon>
            <select [(ngModel)]="selectedEmployeeId">
              <option value="">-- All employees --</option>
              <option *ngFor="let emp of availableEmployees" [value]="emp.id">{{ emp.name }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="filter-row second-row">
        <div class="filter-field">
          <label>Job Types</label>
          <p class="field-hint">Hold Ctrl to select multiple</p>
          <select multiple [(ngModel)]="selectedJobTypes" class="multi-select">
            <option *ngFor="let jt of availableJobTypes" [value]="jt">{{ jt }}</option>
          </select>
        </div>

        <div class="filter-field">
          <label>Work Codes</label>
          <p class="field-hint">Hold Ctrl to select multiple</p>
          <select multiple [(ngModel)]="selectedWorkCodes" class="multi-select">
            <option *ngFor="let wc of workCodes" [value]="wc.code">{{ wc.description }}</option>
          </select>
        </div>
      </div>
    </div>

    <div class="modal-actions">
      <button class="btn-cancel" (click)="showFilterModal = false">Cancel</button>
      <button class="btn-outline" (click)="resetFilters()">
        <mat-icon>restart_alt</mat-icon> Reset
      </button>
      <button class="btn-primary" (click)="applyFilters()">
        <mat-icon>check</mat-icon> Apply
      </button>
    </div>
  </div>
</div>